import React, { useState, useMemo } from 'react';
import InputColor from 'react-input-color';
import { connect } from 'react-redux';
import { Select, Accordion, Button, Tooltip } from '@cimpress/react-components';
import { toSelectOption, getHexColor } from 'src/util/misc';
import { getAllBackgrounds } from 'src/selectors/layers';
import {
    ASSET_ACTIONS_OPTIONS,
    BLENDING_MODES_OPTIONS,
    COLOR_PROFILE_OPTIONS,
    PRODUCT_VARIABLE_TYPES,
    PRODUCT_VARIABLE_TYPES_OPTIONS,
    ASSET_ACTIONS,
    DEFAULT,
    BLENDING_MODES,
} from './constants';
import UploadFile from './UploadFile';
import { showAssetRemovalModal } from '../backgrounds/slice';
import AssetRemovalContainer from './assetRemoveModal';
import { downloadImagesToFileSystem } from 'src/util/upload';
import { getDefaultForAttributeValues, AttributesOption } from './common';
import ErrorIcon from 'src/components/icons/ErrorIcon';
import FinishSelector from 'src/components/finish/finishSelector';
import { AssetActionType } from './service';
import { FinishData } from 'src/services/materialClient';
import { BackgroundType } from 'src/types/background';
import { AppDispatch, RootState } from 'src/store';

const DEFAULTOPTIONS = [{ value: 'Use Default', label: 'Use Default' }];
const actionOptions = DEFAULTOPTIONS.concat(ASSET_ACTIONS_OPTIONS);

export interface OptionType {
    value: string;
    label: string;
}

// `assetOptions` ultimately comes from assets (overlays/backgrounds/document masks) that don't always
// have a name, so unlike `OptionType` above, `value`/`label` here really can be `undefined`.
export interface AssetOption {
    value?: string;
    label?: string;
}

export interface ProductConfigurationValue {
    action: AssetActionType;
    value?: { name?: string; imageUrl?: string } | string;
}

export interface ProductConfiguration {
    layer?: OptionType | null;
    variableAttribute?: string;
    variableOption?: string;
    document?: string | null;
    // The shape stored here depends on `variableOption`: the product/asset variable type stores
    // `ProductConfigurationValue` objects, while blending/color-profile store a plain string and
    // finishes stores a string array of finish ids -- narrow with a cast at the point of use.
    values?: Record<string, unknown>;
    errorMessage?: string;
}

interface Props {
    configuration: ProductConfiguration;
    updateConfiguration: (configuration: ProductConfiguration) => void;
    removeConfiguration: () => void;
    assetOptions: AssetOption[];
    getDocumentOptions: (variableAttribute: string, variableOption: string) => OptionType[];
    showModal: (id: string) => void;
    index: number;
    attributesOptions: AttributesOption[];
    backgroundLayers: BackgroundType[];
}

const isRequiredValuesChosen = (
    variableAttribute: string | undefined,
    variableOption: string | undefined,
    layer: OptionType | null | undefined,
): boolean => {
    if (!variableAttribute) {
        return false;
    }
    if (variableOption === PRODUCT_VARIABLE_TYPES.product && !layer) {
        return false;
    }
    return true;
};

const EachProductVariable = (props: Props) => {
    const { configuration } = props;

    const [nextStage, setNextStage] = useState(
        isRequiredValuesChosen(configuration.variableAttribute, configuration.variableOption, configuration.layer),
    );
    const options = useMemo(() => {
        if (!configuration.variableOption) {
            return [];
        }
        // `toSelectOption`'s declared return type also covers its single-value (string input)
        // overload-less branch; this call always passes an array, so asserting the real array
        // shape here instead of the loose/ambiguous union.
        return toSelectOption(props.attributesOptions.map((option) => option.name)) as OptionType[];
    }, [configuration.variableOption, props.attributesOptions]);
    const onOptionsSelect = (key: string, newValue: unknown) => {
        props.updateConfiguration({ ...configuration, [key]: newValue });
    };
    const onVariableOptionChange = (chosenVariableOption: OptionType) => {
        props.updateConfiguration({
            ...configuration,
            variableOption: chosenVariableOption.value,
            layer: null,
            document: null,
        });
    };
    const allLayers = useMemo(() => {
        if (!configuration.variableAttribute || !configuration.variableOption) {
            return [];
        }
        return props.assetOptions;
    }, [configuration.variableAttribute, configuration.variableOption, props.assetOptions]);
    const documentOptions = useMemo(() => {
        if (!configuration.variableAttribute || !configuration.variableOption) {
            return [];
        }
        return props.getDocumentOptions(configuration.variableAttribute, configuration.variableOption);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuration.variableAttribute, configuration.variableOption, props.getDocumentOptions]);

    const canGoToNextStep = useMemo(() => {
        const { variableAttribute, variableOption, layer } = configuration;
        return isRequiredValuesChosen(variableAttribute, variableOption, layer);
    }, [configuration]);
    const continueNextStep = () => {
        if (canGoToNextStep) {
            setNextStage(true);
            const attributeSelected = props.attributesOptions.find(
                (option) => option.name === configuration.variableAttribute,
            );
            const valueOptions = attributeSelected?.values ?? [];

            let valueDefault: string | string[] | ProductConfigurationValue | undefined;
            switch (configuration.variableOption) {
                case PRODUCT_VARIABLE_TYPES.blending:
                    valueDefault = BLENDING_MODES.Normal;
                    break;
                case PRODUCT_VARIABLE_TYPES.finishes:
                    valueDefault = [];
                    break;
                case PRODUCT_VARIABLE_TYPES.colorProfile:
                    valueDefault = DEFAULT;
                    break;
                case PRODUCT_VARIABLE_TYPES.product:
                    valueDefault = { action: DEFAULT };
            }

            const defaultValues = getDefaultForAttributeValues(valueOptions, valueDefault);
            props.updateConfiguration({ ...configuration, values: defaultValues });
        }
    };
    const deleteConfiguration = () => {
        props.removeConfiguration();
    };

    const onDownload = () => {
        // Clicking download before `continueNextStep` has populated `configuration.values` was already
        // unguarded in the untyped version (it throws), so this doesn't fall back to `{}` here either.
        const values = props.configuration.values as Record<string, unknown>;
        const files = Object.keys(values)
            .map((key) => values[key])
            // Only product/asset values ever carry `value.imageUrl` (blending/finishes/color-profile
            // values are plain strings or string arrays, which safely evaluate `.value` as `undefined`
            // here, same as the untyped version relied on).
            .filter((confgurationValue: any) => confgurationValue.value && confgurationValue.value.imageUrl)
            .map((confgurationValue: any) => ({
                url: confgurationValue.value.imageUrl,
                fileName: confgurationValue.value.name,
            }));

        downloadImagesToFileSystem(
            files,
            `${props.configuration.variableOption}-${props.configuration.variableAttribute}`,
        );
    };

    const getLayerTitle = () => {
        const { variableAttribute, variableOption, layer, document } = configuration;
        let text = 'Please configure attribute';
        if (nextStage && variableAttribute && variableOption) {
            text = `${variableOption} - ${variableAttribute}`;
            if (layer) {
                text += ` - ${layer.label}`;
            } else if (document) {
                text += ` - ${document}`;
            }
        }

        return (
            <div className='layers-header' data-testid={`product-variables-${text}`}>
                {configuration.errorMessage && (
                    <Tooltip direction='top' contents={configuration.errorMessage}>
                        <ErrorIcon />
                    </Tooltip>
                )}
                <div className='layers-header__text truncate_label' title={text}>
                    {text}
                </div>
                <button onClick={onDownload} style={{ marginRight: 8 }}>
                    <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/download-3-l.svg' />
                </button>
                <button onClick={deleteConfiguration}>
                    <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/bin-1-f.svg' />
                </button>
            </div>
        );
    };
    const onActionChange = (valueKey: string, chosenOption: OptionType) => {
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: {
                    action: chosenOption.value as AssetActionType,
                },
            },
        });
    };
    const onBlendingModeChange = (valueKey: string, chosenOption: OptionType) => {
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: chosenOption.value,
            },
        });
    };

    const onFinishesChange = (valueKey: string, chosenOptions: FinishData[]) => {
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: chosenOptions.map((option) => option.id),
            },
        });
    };

    const onColorProfileChange = (valueKey: string, chosenOption: OptionType) => {
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: chosenOption.value,
            },
        });
    };

    const onValueChange = (valueKey: string, value: { name?: string; imageUrl?: string } | string | null) => {
        // `onValueChange` is only wired up from the product/asset variable type's upload/color controls
        // below, so `configuration.values[valueKey]` is always a `ProductConfigurationValue` here despite
        // the looser type on `values`.
        let details: ProductConfigurationValue = {
            ...((configuration.values as Record<string, unknown>)[valueKey] as ProductConfigurationValue),
        };
        if (!value) {
            details = { action: DEFAULT };
        } else {
            details.value = value;
        }
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: details,
            },
        });
    };

    const onRemoveBackground = (valueKey: string) => {
        const id = `${configuration.variableOption};${configuration.variableAttribute};${valueKey}`;
        props.showModal(id);
    };

    const layerDimensions = useMemo(() => {
        if (configuration.layer) {
            const theLayerDetails = props.backgroundLayers.find((each) => each.id === configuration.layer!.value);
            if (theLayerDetails) {
                return {
                    width: theLayerDetails.width,
                    height: theLayerDetails.height,
                };
            }
            return {};
        }
        return {};
    }, [configuration.layer, props.backgroundLayers]);

    return (
        <Accordion className='product-variable-options-list' defaultOpen={true} title={getLayerTitle()}>
            <div className='accordion-body-wrapper'>
                {!nextStage && (
                    <>
                        <div data-testid='product-variables-type-selector'>
                            <Select
                                options={PRODUCT_VARIABLE_TYPES_OPTIONS as OptionType[]}
                                value={toSelectOption(configuration.variableOption ?? '')}
                                onChange={onVariableOptionChange}
                                label='Select Variable Type'
                            />
                        </div>
                        <div data-testid='product-variables-attriubte-selector'>
                            <Select
                                options={options}
                                value={toSelectOption(configuration.variableAttribute ?? '')}
                                onChange={(chosenOption) => onOptionsSelect('variableAttribute', chosenOption.value)}
                                isClearable={false}
                                label='Select Attribute'
                                menuPortalTarget={document.body}
                            />
                        </div>
                        {configuration.variableOption === PRODUCT_VARIABLE_TYPES.product && (
                            <div data-testid='product-variables-layer-selector'>
                                <Select
                                    options={allLayers}
                                    value={configuration.layer}
                                    onChange={(chosenOption) => onOptionsSelect('layer', chosenOption)}
                                    isClearable={false}
                                    isSearchable={false}
                                    label='Select Layer'
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}
                        {(configuration.variableOption === PRODUCT_VARIABLE_TYPES.blending ||
                            configuration.variableOption === PRODUCT_VARIABLE_TYPES.finishes ||
                            configuration.variableOption === PRODUCT_VARIABLE_TYPES.colorProfile) && (
                            <div data-testid='product-variables-document-selector'>
                                <Select
                                    options={documentOptions}
                                    value={toSelectOption(configuration.document ?? '')}
                                    onChange={(chosenOption) => onOptionsSelect('document', chosenOption.value)}
                                    isClearable={false}
                                    isSearchable={false}
                                    label='Select Document'
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}
                        <Button data-testid='product-variables-ok-btn' onClick={continueNextStep}>
                            OK
                        </Button>
                    </>
                )}
                {nextStage &&
                    Object.keys(configuration.values || {}).map((eachValueKey) => {
                        // `nextStage` is only true once `continueNextStep` has populated `configuration.values`,
                        // so it's always defined here despite being optional on the type.
                        const values = configuration.values as Record<string, unknown>;
                        return (
                            <div key={eachValueKey} className='attribute-value-row'>
                                <div className='attribute-value-option'>
                                    {eachValueKey}
                                    {eachValueKey.startsWith('#') && (
                                        <div className='color' style={{ background: eachValueKey }} />
                                    )}
                                </div>
                                {configuration.variableOption === PRODUCT_VARIABLE_TYPES.blending && (
                                    <Select
                                        options={BLENDING_MODES_OPTIONS as OptionType[]}
                                        label='Select'
                                        value={toSelectOption(values[eachValueKey] as string)}
                                        onChange={(chosenOption) => onBlendingModeChange(eachValueKey, chosenOption)}
                                    />
                                )}
                                {configuration.variableOption === PRODUCT_VARIABLE_TYPES.finishes && (
                                    <FinishSelector
                                        id={eachValueKey}
                                        finishes={values[eachValueKey] as string[]}
                                        onSelect={onFinishesChange}
                                    />
                                )}
                                {configuration.variableOption === PRODUCT_VARIABLE_TYPES.colorProfile && (
                                    <Select
                                        options={COLOR_PROFILE_OPTIONS}
                                        label='Select'
                                        value={toSelectOption((values[eachValueKey] as string) || DEFAULT)}
                                        onChange={(chosenOption) => onColorProfileChange(eachValueKey, chosenOption)}
                                    />
                                )}
                                {configuration.variableOption === PRODUCT_VARIABLE_TYPES.product && (
                                    <>
                                        <Select
                                            options={actionOptions}
                                            label='Select'
                                            value={toSelectOption(
                                                (values[eachValueKey] as ProductConfigurationValue).action,
                                            )}
                                            onChange={(chosenOption) => onActionChange(eachValueKey, chosenOption)}
                                            menuPortalTarget={document.body}
                                        />
                                        {(values[eachValueKey] as ProductConfigurationValue).action ===
                                            ASSET_ACTIONS.REPLACE && (
                                            <UploadFile
                                                onChange={(fileDetails) => onValueChange(eachValueKey, fileDetails)}
                                                onBgRemove={() => onRemoveBackground(eachValueKey)}
                                                file={
                                                    (values[eachValueKey] as ProductConfigurationValue).value as
                                                        | { name: string; imageUrl: string }
                                                        | undefined
                                                }
                                                {...layerDimensions}
                                            />
                                        )}
                                        {(values[eachValueKey] as ProductConfigurationValue).action ===
                                            ASSET_ACTIONS.FILL && (
                                            <InputColor
                                                initialValue={
                                                    ((values[eachValueKey] as ProductConfigurationValue).value as
                                                        | string
                                                        | undefined) || '#808080'
                                                }
                                                onChange={(evt) => onValueChange(eachValueKey, getHexColor(evt.hex))}
                                                placement='top'
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
            </div>
            <AssetRemovalContainer />
        </Accordion>
    );
};

function mapStateToProps(state: RootState) {
    const backgroundLayers = getAllBackgrounds(state);

    return {
        backgroundLayers,
    };
}

function mapDispatchToProps(dispatch: AppDispatch) {
    return {
        showModal: (id: string) => dispatch(showAssetRemovalModal(id)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EachProductVariable);
